import { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { AlertTriangle, CheckCircle2, Download } from 'lucide-react';
import api from '@/lib/api';
import { exportCsv } from '@/lib/csv';

const STATUS_COLORS: Record<string, string> = {
  open: '#3B82F6',
  in_progress: '#F59E0B',
  completed: '#10B981',
  cancelled: '#64748B',
};

const PRIORITY_COLORS: Record<string, string> = {
  low: '#06B6D4',
  medium: '#F59E0B',
  high: '#EF4444',
  urgent: '#DC2626',
};

interface Props {
  dateFrom: string;
  dateTo: string;
}

export function TaskReports({ dateFrom, dateTo }: Props) {
  const [byStatus, setByStatus] = useState<any[]>([]);
  const [byPriority, setByPriority] = useState<any[]>([]);
  const [completion, setCompletion] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = { dateFrom, dateTo };
    setLoading(true);
    Promise.all([
      api.get('/reports/tasks/by-status', { params }),
      api.get('/reports/tasks/by-priority', { params }),
      api.get('/reports/tasks/completion', { params }),
    ])
      .then(([s, p, c]) => {
        setByStatus(s.data);
        setByPriority(p.data);
        setCompletion(c.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-[#6366F1] border-t-transparent" />
      </div>
    );
  }

  const handleExport = () => {
    if (byStatus.length)
      exportCsv('tasks_by_status', byStatus, [{ key: 'name', label: 'Status' }, { key: 'value', label: 'Count' }]);
    if (byPriority.length)
      exportCsv('tasks_by_priority', byPriority, [{ key: 'name', label: 'Priority' }, { key: 'value', label: 'Count' }]);
    if (completion)
      exportCsv(
        'tasks_completion',
        [{ metric: 'Total', count: completion.total }, { metric: 'Completed', count: completion.completed }, { metric: 'Overdue', count: completion.overdue }, { metric: 'Completion %', count: completion.completionRate ?? '' }],
        [{ key: 'metric', label: 'Metric' }, { key: 'count', label: 'Value' }],
      );
  };

  const hasAnyData = byStatus.length + byPriority.length + (completion ? 1 : 0) > 0;

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          onClick={handleExport}
          disabled={!hasAnyData}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-200 bg-white text-slate-700 hover:border-[#6366F1] hover:text-[#6366F1] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>
      {/* Completion Stats */}
      {completion && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-[#1E293B]">{completion.total}</div>
            <div className="text-xs text-slate-500 mt-1">Total Tasks</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-emerald-600 flex items-center justify-center gap-1.5">
              <CheckCircle2 className="h-5 w-5" />
              {completion.completed}
            </div>
            <div className="text-xs text-slate-500 mt-1">Completed</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-amber-600 flex items-center justify-center gap-1.5">
              <AlertTriangle className="h-5 w-5" />
              {completion.overdue}
            </div>
            <div className="text-xs text-slate-500 mt-1">Overdue</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm text-center">
            <div className="text-2xl font-bold text-[#6366F1]">{completion.completionRate}%</div>
            <div className="text-xs text-slate-500 mt-1">Completion Rate</div>
            <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#6366F1] rounded-full transition-all"
                style={{ width: `${completion.completionRate}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Tasks by Status */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Tasks by Status</h3>
          {byStatus.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={byStatus} cx="50%" cy="50%" outerRadius={100} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}>
                  {byStatus.map((entry, i) => (
                    <Cell key={i} fill={STATUS_COLORS[entry.name] || '#64748B'} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Tasks by Priority */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <h3 className="text-sm font-semibold text-[#1E293B] mb-4">Tasks by Priority</h3>
          {byPriority.length === 0 ? (
            <p className="text-sm text-slate-400 py-8 text-center">No data available</p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={byPriority}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" radius={[4, 4, 0, 0]} name="Tasks">
                  {byPriority.map((entry, i) => (
                    <Cell key={i} fill={PRIORITY_COLORS[entry.name] || '#6366F1'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
